import { isTermMax } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * TermMax — a fixed-maturity AMM over zero-coupon bonds (FT/XT) with the loan
 * as an ERC-721 (GT).
 *
 * The headline risk is on the LEND side, which is unusual: after maturity plus
 * a 7,200 s liquidation window, unpaid collateral is distributed pro-rata to
 * FT holders and SOCIALISED across the whole FT supply. A lender in a market
 * that had any default is affected regardless of which loan defaulted, and can
 * be settled in collateral rather than the asset they lent.
 */
export const termMaxAdapter: TermAdapter = {
  id: 'termmax',
  matches: isTermMax,
  profileId: () => 'termmax.fixed@v1',
  build: () => ({
    supply: {
      exit: {
        mode: 'market-sale',
        settlement: 'sync',
        // Selling FT needs a maker order on the other side, which may not exist.
        priceRisk: 'may-be-impossible',
        partialAllowed: true,
      },
      counterparty: {
        kind: 'orderbook',
        solvency: 'overcollateralized',
        socializedLoss: true,
      },
      principal: {
        protected: false,
        risks: ['physical-delivery', 'bad-debt'],
      },
      maturity: { atMaturity: 'physical-delivery' },
    },
    borrow: {
      // A raw uint128 face value with zero accrual.
      debtShape: 'static-face',
      exit: {
        earlyRepay: 'free',
        atMaturityCost: 'face',
        lateBehaviour: 'liquidatable',
        partialAllowed: true,
      },
      maturity: { atMaturity: 'physical-delivery', graceSecs: 7200 },
      liquidation: {
        // Unpaid collateral is DELIVERED to bond holders after the window —
        // not repaid-and-seized by a liquidator.
        model: 'delivery',
        absorber: 'lenders',
        trigger: 'price-and-time',
        seizure: 'proportional',
        windowSecs: 7200,
        badDebt: 'socialized',
      },
      counterparty: { kind: 'orderbook', solvency: 'overcollateralized' },
    },
    constraints: {
      positionModel: 'nft',
      positionIdMeaning: 'The GT (loan) ERC-721 token id.',
    },
  }),
}
